import { ServiceBase } from "@app/shared";
import { AddCodeMaps, CodeMapItem, CodeMapSetModel, CodeMapSetModelWithActiveProjects, CodeMapSetVersionModel, CodeMapTypeEnum } from "./CodeMapSetModels";
import { querystringify } from "@app/shared/utility";

/** Manages interaction with the codeMap set API. */
export default class CodeMapSetService extends ServiceBase {
  /** Retrieves all codeMap sets of the given type for the logged-in user. */
  public async get(codeMapType: CodeMapTypeEnum): Promise<CodeMapSetModel[]> {
    return await this.makeGet<CodeMapSetModel[]>(`codeMapsets/type/${codeMapType}`);
  }
  public async getOnlyClientCodeMaps(codeMapType: CodeMapTypeEnum): Promise<CodeMapSetModel[]> {
    return await this.makeGet<CodeMapSetModel[]>(`codeMapsets/client-only/types/${codeMapType}`);
  }
  /**
   * Retrieves a codeMap set corresponding to a given identifier.
   * @param id The codeMap set's identifier.
   */
  public async getById(id: number): Promise<CodeMapSetModel> {
    return await this.makeGet<CodeMapSetModel>(`codeMapsets/${id}`);
  }

  /**
   * Creates a new codeMap set with a given name and description.
   * @param name The name of the codeMap set.
   * @param description The description of the codeMap set.
   */
  public async create(name: string, description: string, isPrivate: boolean): Promise<void> {
    await this.makePost("codeMapsets", { description, isPrivate, name });
  }

  public async AddCodeMapSetVersion(id: number): Promise<CodeMapSetVersionModel> {
    return await this.makePost<CodeMapSetVersionModel>(`codeMapsets/${id}/versions`);
  }

  public async AddCodeMapItems(id: number, versionId: number, codeMapItems: AddCodeMaps): Promise<void> {
    await this.makePost(`codeMapsets/${id}/versions/${versionId}`, codeMapItems);
  }

  public async ArchiveCodeMapSetVersion(id: number, versionId: number): Promise<void> {
    await this.makePut(`codeMapsets/${id}/versions/${versionId}/archive`);
  }

  public async ActivateCodeMapSetVersion(id: number, versionId: number): Promise<void> {
    await this.makePut(`codeMapsets/${id}/versions/${versionId}/activate`);
  }

  public async ArchiveCodeMapSetAndCodeMapSetVersions(id: number): Promise<void> {
    await this.makePut(`codeMapsets/${id}/archive-code-map-set-and-versions`);
  }

  public async ActivateCodeMapSetAndCodeMapSetVersions(id: number): Promise<void> {
    await this.makePut(`codeMapsets/${id}/activate-code-map-set-and-versions`);
  }

  public async getCodeMapItems(codeMapSetId : number): Promise<CodeMapItem[]> {
    return await this.makeGet<CodeMapItem[]>(`/codeMapsets/code-map-items/${codeMapSetId}`);
  }

  public async GetEditTemplateOptionsCodeMapData(codeMapTypes: CodeMapTypeEnum[], includeVersions: boolean, includeOnlyClientAssigned: boolean, includeArchived: boolean): Promise<CodeMapSetModelWithActiveProjects[]> {
    const query = {
      codeMapTypes,
      includeArchived,
      includeOnlyClientAssigned,
      includeVersions
    }
    return await this.makeGet<CodeMapSetModelWithActiveProjects[]>(`/codeMapsets/GetEditTemplateOptionsCodeMapData?${querystringify(query)}`);
  }
}
